/**
 * Subscription service for managing Stripe subscriptions and promotions
 */

import { directApi } from './api';

export const subscriptionService = {
  /**
   * Create a Stripe checkout session
   */
  createCheckoutSession: async (plan, promotionCode = null) => {
    try {
      const response = await directApi.post('/subscriptions/create-checkout-session', {
        plan,
        promotion_code: promotionCode
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to create checkout session');
    }
  },

  /**
   * Create a Stripe customer portal session
   */
  createPortalSession: async () => {
    try {
      const response = await directApi.post('/subscriptions/create-portal-session');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to create portal session');
    }
  },

  /**
   * Get current subscription information
   */
  getCurrentSubscription: async () => {
    try {
      // Add cache busting parameter to force fresh data
      const cacheBuster = `_=${Date.now()}`;
      const response = await directApi.get(`/subscriptions/current?${cacheBuster}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to get subscription');
    }
  },

  /**
   * Validate a promotion code (public endpoint - no authentication required)
   */
  validatePromotionCode: async (code, plan = 'plus') => {
    try {
      const response = await directApi.post('/promotions/validate-public', {
        code: code.toUpperCase(),
        plan
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to validate promotion code');
    }
  },

  /**
   * Apply a promotion code
   */
  applyPromotionCode: async (code, subscriptionData = {}) => {
    try {
      const response = await directApi.post('/promotions/apply', {
        code: code.toUpperCase(),
        subscription_data: subscriptionData
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to apply promotion code');
    }
  },

  /**
   * Get active promotions
   */
  getActivePromotions: async () => {
    try {
      const response = await directApi.get('/promotions/active');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to get promotions');
    }
  },

  /**
   * Get user's current promotion status
   */
  getUserPromotionStatus: async () => {
    try {
      const response = await directApi.get('/promotions/user-status');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to get promotion status');
    }
  }
};

export default subscriptionService;
